import { adminConfig } from '../config/env';

const scopes = ['openid', 'profile', 'account', 'admin:access'];
const transactionKey = 'kkutuio-admin.oauth.transaction';
const tokenKey = 'kkutuio-admin.oauth.token';
const sessionChannelName = 'kkutuio-admin.oauth.session.v1';
const sessionRestoreTimeoutMs = 400;

export const OAUTH_SESSION_CLEARED_EVENT = 'kkutu-admin-oauth-session-cleared';

interface OAuthTransaction {
  state: string;
  nonce: string;
  verifier: string;
  returnPath: string;
}

interface StoredToken {
  accessToken: string;
  expiresAt: number;
}

type OAuthSessionMessage = {
  type: 'token-request';
  sourceId: string;
  requestId: string;
} | {
  type: 'token-response';
  sourceId: string;
  requestId: string;
  token: StoredToken;
} | {
  type: 'session-cleared';
  sourceId: string;
};

interface OAuthTokenResponse {
  access_token?: unknown;
  expires_in?: unknown;
  error?: unknown;
  error_description?: unknown;
}

const encodeUrl = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const random = (size: number): string => {
  if (!window.crypto?.getRandomValues) {
    throw new Error('이 브라우저는 안전한 난수 생성을 지원하지 않습니다. 최신 브라우저를 사용하세요.');
  }
  const bytes = new Uint8Array(size);
  window.crypto.getRandomValues(bytes);
  return encodeUrl(bytes);
};

let sessionSourceId: string | null = null;
let sessionChannel: BroadcastChannel | null = null;
const pendingTokenRequests = new Map<string, (token: StoredToken | null) => void>();

const getSessionSourceId = (): string => {
  if (!sessionSourceId) {
    try {
      sessionSourceId = random(16);
    } catch {
      sessionSourceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
  }
  return sessionSourceId;
};

async function challengeFor(verifier: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error('이 브라우저는 OAuth PKCE를 지원하지 않습니다. 최신 브라우저를 사용하세요.');
  }
  const bytes = new TextEncoder().encode(verifier);
  return encodeUrl(new Uint8Array(await window.crypto.subtle.digest('SHA-256', bytes)));
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function removeStoredValue(key: string): void {
  try {
    sessionStore()?.removeItem(key);
  } catch {
    // Storage can be disabled by browser privacy or enterprise policy.
  }
}

function parseStoredValue<T>(key: string): T | null {
  const storage = sessionStore();
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    removeStoredValue(key);
    return null;
  }
}

function isValidStoredToken(value: unknown): value is StoredToken {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const token = value as Partial<StoredToken>;
  return typeof token.accessToken === 'string'
    && token.accessToken.length > 0
    && token.accessToken.length <= 32_768
    && Number.isFinite(token.expiresAt)
    && Number(token.expiresAt) > Date.now();
}

function readStoredToken(): StoredToken | null {
  const saved = parseStoredValue<StoredToken>(tokenKey);
  if (isValidStoredToken(saved)) return saved;
  removeStoredValue(tokenKey);
  return null;
}

function openSessionChannel(): BroadcastChannel | null {
  if (sessionChannel) return sessionChannel;
  if (typeof window.BroadcastChannel !== 'function') return null;

  const sourceId = getSessionSourceId();
  sessionChannel = new window.BroadcastChannel(sessionChannelName);
  sessionChannel.addEventListener('message', ({ data }: MessageEvent<unknown>) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return;
    const message = data as Partial<OAuthSessionMessage>;
    if (message.sourceId === sourceId) return;

    if (message.type === 'token-request' && typeof message.requestId === 'string') {
      const token = readStoredToken();
      if (token) {
        sessionChannel?.postMessage({
          type: 'token-response', sourceId, requestId: message.requestId, token,
        } satisfies OAuthSessionMessage);
      }
      return;
    }

    if (message.type === 'token-response' && typeof message.requestId === 'string') {
      const resolve = pendingTokenRequests.get(message.requestId);
      if (resolve && isValidStoredToken(message.token)) resolve(message.token);
      return;
    }

    if (message.type === 'session-cleared') {
      removeStoredValue(tokenKey);
      removeStoredValue(transactionKey);
      window.dispatchEvent(new Event(OAUTH_SESSION_CLEARED_EVENT));
    }
  });
  return sessionChannel;
}

export function startOAuthSessionSync(): void {
  openSessionChannel();
}

export async function restoreOAuthSessionFromPeer(
  timeoutMs = sessionRestoreTimeoutMs,
): Promise<boolean> {
  if (readStoredToken()) return true;
  const channel = openSessionChannel();
  if (!channel) return false;

  const requestId = random(16);
  return new Promise(resolve => {
    let settled = false;
    const finish = (token: StoredToken | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      pendingTokenRequests.delete(requestId);
      if (token && isValidStoredToken(token)) {
        const storage = sessionStore();
        if (storage) {
          storage.setItem(tokenKey, JSON.stringify(token));
          resolve(true);
        } else {
          resolve(false);
        }
      } else {
        resolve(false);
      }
    };
    const timer = window.setTimeout(() => finish(null), Math.max(0, timeoutMs));
    pendingTokenRequests.set(requestId, finish);
    channel.postMessage({
      type: 'token-request', sourceId: getSessionSourceId(), requestId,
    } satisfies OAuthSessionMessage);
  });
}

export function stopOAuthSessionSync(): void {
  pendingTokenRequests.forEach(resolve => resolve(null));
  pendingTokenRequests.clear();
  sessionChannel?.close();
  sessionChannel = null;
  sessionSourceId = null;
}

export function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return false;
  if (value.includes('\\') || Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint < 32 || codePoint === 127;
  })) return false;
  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

export const oauthCallbackPath = new URL(adminConfig.oauthRedirectUri).pathname;
export const isOAuthCallback = (): boolean => window.location.pathname === oauthCallbackPath;

export function getAccessToken(): string | null {
  return readStoredToken()?.accessToken ?? null;
}

export function clearOAuthSession(): void {
  removeStoredValue(tokenKey);
  removeStoredValue(transactionKey);
  const channel = openSessionChannel();
  if (channel) {
    channel.postMessage({
      type: 'session-cleared', sourceId: getSessionSourceId(),
    } satisfies OAuthSessionMessage);
  }
}

export async function beginOAuthLogin(returnPath = '/'): Promise<void> {
  const state = random(32);
  const nonce = random(32);
  const verifier = random(64);
  const challenge = await challengeFor(verifier);
  const safeReturnPath = isSafeReturnPath(returnPath) ? returnPath : '/';
  const storage = sessionStore();
  if (!storage) {
    throw new Error('브라우저의 세션 저장소를 사용할 수 없습니다. 쿠키·사이트 데이터 차단 설정을 확인하세요.');
  }
  storage.setItem(transactionKey, JSON.stringify({ state, nonce, verifier, returnPath: safeReturnPath }));
  const authorize = new URL(`${adminConfig.oauthIssuer}/oauth/authorize`);
  authorize.search = new URLSearchParams({
    client_id: adminConfig.oauthClientId,
    redirect_uri: adminConfig.oauthRedirectUri,
    response_type: 'code',
    scope: scopes.join(' '),
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }).toString();
  window.location.assign(authorize.toString());
}

export async function finishOAuthLogin(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  const callbackError = params.get('error');
  if (callbackError) {
    removeStoredValue(transactionKey);
    throw new Error(params.get('error_description') || callbackError);
  }

  const code = params.get('code');
  const state = params.get('state');
  const transaction = parseStoredValue<OAuthTransaction>(transactionKey);
  if (!code || !state || !transaction || state !== transaction.state
    || typeof transaction.verifier !== 'string') {
    removeStoredValue(transactionKey);
    throw new Error('OAuth 로그인 응답을 검증할 수 없습니다. 다시 시도하세요.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: adminConfig.oauthClientId,
    code,
    redirect_uri: adminConfig.oauthRedirectUri,
    code_verifier: transaction.verifier,
  });

  try {
    const response = await fetch(`${adminConfig.oauthIssuer}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const payload = await response.json().catch(() => ({})) as OAuthTokenResponse;
    if (!response.ok || typeof payload.access_token !== 'string' || !payload.access_token) {
      const message = typeof payload.error_description === 'string'
        ? payload.error_description
        : typeof payload.error === 'string' ? payload.error : 'OAuth 토큰을 발급하지 못했습니다.';
      throw new Error(message);
    }
    const expiresIn = Number(payload.expires_in);
    const storage = sessionStore();
    if (!storage) throw new Error('브라우저의 세션 저장소를 사용할 수 없습니다.');
    storage.setItem(tokenKey, JSON.stringify({
      accessToken: payload.access_token,
      expiresAt: Date.now() + Math.max(60, Number.isFinite(expiresIn) ? expiresIn : 0) * 1000 - 30_000,
    } satisfies StoredToken));
    return isSafeReturnPath(transaction.returnPath) ? transaction.returnPath : '/';
  } finally {
    removeStoredValue(transactionKey);
  }
}
